import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import qs from 'query-string';
import axios from '../../axios';

import PasswordResetDialog from '../../components/Auth/PasswordResetDialog/PasswordResetDialog';
import Alert, { EAlertColor } from '../../components/UI/Alert/Alert';
import Button, { EButtonColor } from '../../components/UI/Button/Button';
import  { EInputType, IInputField } from '../../components/UI/Input/Input';
import { useInputs } from '../../components/UI/Input/useInputs';
import ModalContext, { EModalSize } from '../../components/UI/Modal/ModalContext';
import Spinner from '../../components/UI/Spinner/Spinner';
import { EInputUpdateAction } from '../../context/InputContext';
import { IAppState, IAuthState } from '../../interfaces';

import * as actions from '../../store/actions';
import { ETranslation } from '../../translations/translation-keys';
import classes from './Auth.module.scss';
import { useLocation } from 'react-router';

import { auth } from '../../firebase';
import { signInWithCustomToken } from 'firebase/auth';
import i18n from '../../i18n';

export enum EAuth {
  username = "username",
  password = "password",
};

const Auth: React.FC = () => {
  const { setModal, closeModal } = useContext(ModalContext);
  const dispatch = useDispatch();

  const location = useLocation();

  const { loading, error, customToken } = useSelector<IAppState, IAuthState>((state) => state.auth);

  const { t } = useTranslation();

  useEffect(() => {
    const params = qs.parse(location.search);
    if (params.t) {
      dispatch(actions.getCustomToken(params.t as string));
    }

  }, [location, dispatch]);

  useEffect(() => {
    if (customToken) {
      signInWithCustomToken(auth, customToken).catch((err) => {
        console.error("Custom token login failed", err);
        const params = qs.parse(location.search);
        if (params.t) {
          axios.post('/json/auth/logTokenError', {
            token: params.t as string,
            error: err.message || String(err)
          }).catch(logErr => console.error("Failed to send error log to backend", logErr));
        }
      });
    }
  }, [customToken, location.search]);

  const [inputs, setInputs] = useState<IInputField>({
    [EAuth.username]: {
      type: EInputType.email,
      labelTranslation: ETranslation.USERNAME,
      placeholderTranslation: ETranslation.USERNAME,
      value: "",
    },
    [EAuth.password]: {
      type: EInputType.password,
      labelTranslation: ETranslation.PASSWORD,
      placeholderTranslation: ETranslation.PASSWORD,
      value: "",
    },
  });

  const { createInput: createAuthInput, submit } = useInputs({
    inputs,
    setInputs,
    options: {
      updateAction: EInputUpdateAction.AUTH,
    }
  });

  const submitHandler = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = await submit();
    if (!data) return;
    const username = (data as any)[EAuth.username] as string;
    const password = (data as any)[EAuth.password] as string;
    const preferredLanguage = i18n.language;
    dispatch(actions.auth(username, password, preferredLanguage));
  };

  const loginWithGoogleHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    const preferredLanguage = i18n.language;
    dispatch(actions.authWithGoogle(preferredLanguage));
  };

  const setPasswordResetDialog = () => setModal({
    isOpen: true,
    title: t(ETranslation.RESET_PASSWORD),
    size: EModalSize.SMALL,
    content: <PasswordResetDialog onCancel={closeModal} />,
  })

  return (
    <div className={classes.Container}>
      <div className={classes.LoginBox}>
        {new Date() < new Date('2026-08-18') && (
          <Alert color={EAlertColor.SUCCESS} style={{ marginBottom: '1rem', fontSize: '0.9rem', lineHeight: '1.5' }}>
            Energiatodistuksen laatijat: tarjouspyyntöportaali (Hanki Energiatodistus admin) on uudistunut ja vaihtanut paikkaa tänne! Tarkista sähköpostisi, jossa on ohjeet kirjautumiseen.
          </Alert>
        )}
        <div className={classes.Title}>{t(ETranslation.TITLE_HANKIX)}</div>
        {(loading && <Spinner />) || (
          <form onSubmit={submitHandler}>
            <div className={classes.Inputs}>
              {createAuthInput(EAuth.username)}
              {createAuthInput(EAuth.password)}
            </div>
            <div className={classes.ButtonContainer}>
              <Button onClick={submitHandler} className={classes.Button} type="submit">
                {t(ETranslation.LOGIN)}
              </Button>
            </div>
            
            <div className={classes.ButtonContainer} style={{ marginTop: '1rem' }}>
              <Button onClick={loginWithGoogleHandler} className={classes.Button} type="button">
                {t(ETranslation.LOGIN_GOOGLE)}
              </Button>
            </div>
            
            <div className={classes.ButtonContainer}>
            {i18n.language === "fi" ? (
                <Button onClick={() => i18n.changeLanguage('en')} color={EButtonColor.NONE}>{t(ETranslation.LANGUAGE_IN_ENGLISH)}</Button>
              ) : (
                <Button onClick={() => i18n.changeLanguage('fi')} color={EButtonColor.NONE}>{t(ETranslation.LANGUAGE_IN_FINNISH)}</Button>
              )}
              <Button className='passwordResetButton' onClick={setPasswordResetDialog} color={EButtonColor.NONE}>
                {t(ETranslation.FORGOT_PASSWORD)}
              </Button>
            </div>
            {error && (
              <Alert className={classes.ErrorMessage}>{error}</Alert>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default Auth;

